
import Vue from "vue";
import Component from "vue-class-component";
import { EditCommodityModel } from "@/models/commodities";
import Select from "@/components/form/Select.vue";
import {
  AddOrderCommodityRequest,
  EditOrderCommodityRequest,
  EditOrderCommodityUnitsRequest
} from "../../../models/api/orders/commodities";
import notifications from "../../../lib/notifications";
import SystemModule from "@/store/modules/system";
import OrderModule from "@/store/modules/order";
import { getModule } from "vuex-module-decorators";
import permissions from "@/security/permissions";
import * as modals from "@/lib/modals";
import { Prop } from "vue-property-decorator";

const systemModuleState = getModule(SystemModule);
const orderModule = getModule(OrderModule);

@Component({
  name: modals.editCommodityModal,
  components: { Select }
})
export default class EditCommodityModal extends Vue {
  model: EditCommodityModel = null;
  visible: boolean = false;
  saving: boolean = false;
  availableCommodities: any[] = null;

  get fixForRules() {
    return this.model.fixRate ? "required" : null;
  }

  get fixForOptions() {
    return systemModuleState.commodityFixForOptions.map(x => ({
      label: x.description,
      value: x.code
    }));
  }

  async show(commodity) {
    this.model = new EditCommodityModel(commodity);
    if (orderModule.currentOrder.billTo) {
      try {
        const results = await systemModuleState.searchCommoditiesByCode({
          code: null,
          billTo: orderModule.currentOrder.billTo.id,
          deliveryForId: this.deliveryForId
        });

        this.availableCommodities = results.map(x => ({ label: x.agentId, value: x.code }));
      } catch (ex) {
        console.error("Failed to load commodities", ex);
      }
    }
    this.visible = true;
  }

  async save() {
    const request = this.createRequestModel();
    let action = orderModule.addCommodity;
    if (!this.model.isNew)
      if (this.hasPermission(permissions.order.edit.commodity)) action = orderModule.editCommodity;
      else action = orderModule.editCommodityUnits;

    try {
      this.saving = true;
      await action(request).then(() => {
        notifications.success(
          `${this.model.isNew ? "Add" : "Edit"} Commodity`,
          `Commodity successfully ${this.model.isNew ? "added" : "modified"}`
        );
        this.$emit("updated");
        this.hide();
      });
    } catch (ex) {
      notifications.error("Save Commodity", "Failed to save commodity", ex);
    }

    this.saving = false;
  }
  get deliveryForId() {
    return orderModule.currentOrder.deliveryForId;
  }

  get billToId() {
    return orderModule.currentOrder.billTo.id;
  }

  createRequestModel():
    | AddOrderCommodityRequest
    | EditOrderCommodityRequest
    | EditOrderCommodityUnitsRequest {
    if (this.model.isNew) {
      const request: AddOrderCommodityRequest = {
        commodityCode: this.model.commodityCode,
        description: this.model.description,
        count: +this.model.count,
        chargeQuantity: +this.model.chargeQuantity,
        countUnit: this.model.countUnit,
        volume: +this.model.volume,
        volumeUnit: this.model.volumeUnit,
        weight: +this.model.weight,
        weightUnit: this.model.weightUnit,
        fixFor: +this.model.fixFor,
        fixRate: this.model.fixRate,
        fixDollars: this.model.fixDollars,
        rate: +this.model.rate,
        charge: +this.model.charge
      };
      return request;
    }

    if (this.hasPermission(permissions.order.edit.commodity)) {
      const request: EditOrderCommodityRequest = {
        originalCommodityCode: this.model.originalCommodityCode,
        commodityCode: this.model.commodityCode,
        description: this.model.description,
        count: +this.model.count,
        chargeQuantity: +this.model.chargeQuantity,
        countUnit: this.model.countUnit,
        volume: +this.model.volume,
        volumeUnit: this.model.volumeUnit,
        weight: +this.model.weight,
        weightUnit: this.model.weightUnit,
        fixFor: +this.model.fixFor,
        fixRate: this.model.fixRate,
        fixDollars: this.model.fixDollars,
        rate: +this.model.rate,
        charge: +this.model.charge
      };
      return request;
    }

    const request: EditOrderCommodityUnitsRequest = {
      commodityCode: this.model.commodityCode,
      count: +this.model.count,
      countUnit: this.model.countUnit,
      volume: +this.model.volume,
      volumeUnit: this.model.volumeUnit,
      weight: +this.model.weight,
      weightUnit: this.model.weightUnit
    };

    return request;
  }

  hide() {
    this.visible = false;
    this.model = null;
    this.availableCommodities = null;
  }

  get title() {
    if (!this.model) return "";
    if (this.model.isNew) {
      return "Add Order Commodity";
    }
    return "Edit Order Commodity";
  }
}
